'use client';

import React, { createContext, useContext } from 'react';

import { isEmpty } from 'lodash';

import { User } from '@/app/actions/auth/types';
import { AddCardForm, ConfirmationForm } from '@/app/actions/bank-card/types';
import { ChildrenProps } from '@/types/common.types';

import { useVerification } from '@/contexts/VerificationContext/useVerification';

import { UserApi } from '@/api/domains/user-api';

import { UZ_PHONE_CODE } from '@/constants/phone-number.constants';

import { useNotification } from '@/contexts/NotificationContext';
import { useStore } from '@/contexts/StoreContext';

// =================================================================

interface BankCardProviderProps extends ChildrenProps {
  responseMessage: string;
  data: any;
  hasOTP: boolean;
  setData: Function;
  setHasOTP: (hasOTP: boolean) => void;
  setResponseMessage: (responseMessage: string) => void;

  handleSubmitCardInformation: (data: AddCardForm) => void | Promise<void>;
  handleSubmitConfirmation: (formData: ConfirmationForm) => void | Promise<void>;
}

// =================================================================

const BankCardContext = createContext<Omit<BankCardProviderProps, 'children'> | null>(null);

const BankCardProvider = (
  props: Omit<
    BankCardProviderProps,
    | 'purchaseSubscriptionWithCard'
    | 'purchaseSubscriptionWithBalance'
    | 'handleSubmitCardInformation'
    | 'handleSubmitConfirmation'
    | 'isPending'
  >,
) => {
  const { children, setHasOTP, setData, setResponseMessage, ...rest } = props;

  const { userCredentials, setUserCredentials } = useVerification();
  const { notification } = useNotification();

  const { subscription } = useStore(store => store.subscriptionStore);

  // Add card Information
  const handleSubmitCardInformation = async (data: AddCardForm) => {
    const transformedBankCardNumber = data.card_number.split(' ').join('');

    const transformedExpireDate = data.card_exp.split('/').reverse().join('').trim();

    const normalizedPhone = UZ_PHONE_CODE + data.user_phone.replace(/\s/g, '');
    try {
      const { message } = await UserApi.addBankCard({
        card_exp: transformedExpireDate,
        card_number: transformedBankCardNumber,
        user_phone: normalizedPhone,
      });

      setData({
        card_number: transformedBankCardNumber,
        card_exp: transformedExpireDate,
      });
      setHasOTP(true);
      setResponseMessage(message);
    } catch (error) {
      if (!error.response) {
        throw error;
      }

      const { message } = error.response;
      notification('error', message);
    }
  };

  // Confirmation code Submit
  const handleSubmitConfirmation = async (formData: ConfirmationForm) => {
    try {
      const response = await UserApi.cardConfrimOTP(formData.code);
      const userData = { ...userCredentials, card: response.data } as User;
      setUserCredentials(userData);

      if (isEmpty(subscription)) {
        setHasOTP(false);
      } else {
        setHasOTP(true);
        // await purchaseSubscriptionWithCard();
      }
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      const { message } = error.response;
      notification('error', message);
    }
  };

  const value = {
    ...rest,
    setHasOTP,
    setData,
    setResponseMessage,
    handleSubmitCardInformation,
    handleSubmitConfirmation,
  };

  return <BankCardContext.Provider value={value}>{children}</BankCardContext.Provider>;
};

export default BankCardProvider;

// =================================================================

export const useBankCard = () => {
  const context = useContext(BankCardContext);

  if (!context) {
    throw new Error('useBankCard should be used within BankCardProvider');
  }

  return context;
};
